import { useRef, useState } from "react";
import { makeStyles } from "@material-ui/styles";
import BoxControls from "../../Common/Box/BoxControls";
import Box from "../../Common/Box/Box";
import Chart from "../../Common/Box/Chart";
import Legend from "../../Common/Box/Legend";
import { chartDownloadImageScaleFactor } from "../../../constants";
import initialData from "../../../data/initialData.json";
import exportCharts from "../../../export";
import useCsvDownload from "../../../hooks/useCsvDownload";
import theme from "../../../theme";
import { getTooltip } from "../../../utilities";

const key = "APAC_f5";
const number = 5;

const getMaximumValue = (data) => {
  let maximumValue = 0;

  for (let incomeGroup of data) {
    for (let scenarioIndex = 0; scenarioIndex < 3; scenarioIndex++) {
      let scenarioTotal = 0;

      for (let method of incomeGroup.methods) {
        scenarioTotal += method.data[scenarioIndex];
      }

      maximumValue = Math.max(maximumValue, scenarioTotal);
    }
  }

  return maximumValue;
};

const getSeriesColor = (name, theme) => {
  switch (name) {
    case "Surgical: Vacuum aspiration":
      return theme.palette.areas.abortion.methods.mva;

    case "Medical: Miso only":
      return theme.palette.areas.abortion.methods.miso;

    case "Medical: Miso & mife":
      return theme.palette.areas.abortion.methods.mife;

    case "Surgical: Dilation (not costed)":
      return theme.palette.areas.abortion.methods.dilation;

    case "Least safe abortions (not costed)":
      return theme.palette.areas.abortion.methods.leastSafeAbortions;

    default:
      return "black";
  }
};

const options = (data, props) => (theme) => {
  const absolute = props.comparisonMode !== "relative";

  const series = data.methods.map((method) => ({
    ...method,
    color: getSeriesColor(method.name, theme),
  }));

  return {
    chart: {
      type: "column",
      height: 350,
      backgroundColor: null,
      width: props.showYAxis ? 297 : 250,
      spacing: [10, 0, 15, 0],
    },
    title: {
      text: data.incomeGroup,
      align: "center",
      x: props.showYAxis ? 22 : undefined,
    },
    xAxis: {
      categories: [
        "1: Maintain safety profile & method mix",
        "2: Shift safety profile",
        "3: Shift safety profile & method mix",
      ],
      labels: {
        autoRotation: undefined,
        useHTML: true,
        style: {
          textAlign: "center",
        },
      },
    },
    yAxis: {
      title: undefined,
      min: 0,
      max: absolute ? props.yAxisMaximum : 100,
      reversedStacks: false,
      labels: {
        enabled: props.showYAxis,
        format: absolute ? undefined : "{value}%",
        style: {
          color: theme.figure.axis.secondary,
          fontSize: theme.figure.axis.fontSize,
        },
      },
    },
    plotOptions: {
      column: {
        stacking: absolute ? "normal" : "percent",
        pointWidth: 30,
        borderWidth: 0,
      },
    },
    tooltip: {
      backgroundColor: "white",
      useHTML: true,
      outside: true,
      formatter: function () {
        return getTooltip("Services", this.y, this.percentage);
      },
      style: {
        fontSize: "0.875rem",
        color: theme.palette.tooltip.primary,
      },
    },
    legend: {
      enabled: false,
    },
    exporting: {
      chartOptions: {
        title: {
          text: data.incomeGroup,
        },
      },
      enabled: false,
    },
    series,
  };
};

const useStyles = makeStyles((theme) => ({
  content: {
    alignItems: "center",
    flexDirection: "column",
    display: "flex",
  },
  facet: {
    margin: theme.spacing(0, 3),
  },
  facetPanel: {
    display: "flex",
  },
  legend: {
    marginTop: theme.spacing(),
  },
}));

const Component = () => {
  const [comparisonMode, setComparisonMode] = useState("relative");

  const highchartRefs = [useRef(), useRef(), useRef()];

  const downloadCsv = useCsvDownload();

  const classes = useStyles();

  const data = initialData["apac_f5"];

  const handleComparisonModeToggle = () => {
    setComparisonMode((comparisonMode) =>
      comparisonMode === "absolute" ? "relative" : "absolute"
    );
  };

  const handleCsvDownload = () => {
    downloadCsv(`/apac/figures/5/csv`, `apac_f${number}.csv`);
  };

  const yAxisMaximum = getMaximumValue(data) * 1.02;

  return (
    <Box
      id={key}
      caption={`Figure ${number}`}
      title="Future Abortion and PAC Services by Method"
      subtitle="By Future Scenario and Income Group, 2030"
      controls={
        <BoxControls
          comparisionMode={comparisonMode}
          onToggleComparisonMode={handleComparisonModeToggle}
        />
      }
      onDownloadCsv={handleCsvDownload}
      onDownloadPng={() => {
        exportCharts(
          {
            chart1: highchartRefs[0].current,
            chart2: highchartRefs[1].current,
            chart3: highchartRefs[2].current,
          },
          {
            filename: key,
            scale: chartDownloadImageScaleFactor,
          }
        );
      }}
    >
      <div className={classes.content}>
        <div className={classes.facetPanel}>
          {data.map((incomeGroup, index) => (
            <div key={incomeGroup.incomeGroup} className={classes.facet}>
              <Chart
                highchartRef={highchartRefs[index]}
                options={options(incomeGroup, {
                  yAxisMaximum,
                  showYAxis: index === 0,
                  comparisonMode,
                })}
              />
            </div>
          ))}
        </div>

        <div className={classes.legend}>
          <Legend
            series={[
              {
                name: "Surgical: Vacuum aspiration",
                color: getSeriesColor("Surgical: Vacuum aspiration", theme),
              },
              {
                name: "Medical: Miso only",
                color: getSeriesColor("Medical: Miso only", theme),
              },
              {
                name: "Medical: Miso & mife",
                color: getSeriesColor("Medical: Miso & mife", theme),
              },
              {
                name: "Surgical: Dilation (not costed)",
                color: getSeriesColor("Surgical: Dilation (not costed)", theme),
              },
              {
                name: "Least safe abortions (not costed)",
                color: getSeriesColor(
                  "Least safe abortions (not costed)",
                  theme
                ),
              },
            ]}
            markerStyle="circle"
            orientation="horizontal"
          />
        </div>
      </div>
    </Box>
  );
};

export default Component;
